// TODO add hook to reuse hoverID setting and cbs for mouse events
// TODO use useUpdate to create the mesh ref
import * as THREE from "three";
import React, {
  useCallback,
  useEffect,
  useState,
  useRef,
  useMemo,
} from "react";
import { useFrame, useResource } from "react-three-fiber";
import { a } from "@react-spring/three";
import { config, useSpring } from "@react-spring/core";

import {
  useMemoAttributeArrays,
  useTransitionAttributeArray,
} from "../../utils";
import { InstancedPhongMaterial } from "../materials/custom";
import { Tooltip } from "./Tooltip";

// const AnimatedInstancedPhongMaterial = animated(InstancedPhongMaterial);

// TODO Animate x/y position using buffer attributes
// TODO Event handler callbacks here are probably causing problems
// TODO Refactor prop name so it's apparent when we talk about click events and hovers
export default function Spheres({
  data,
  x,
  y,
  z,
  selected = [],
  onHover = () => {},
  opacityFunc = (d) => d.opacity,
  colorFunc,
  size = 0.05,
  name = ``,
  makeHole = false,
  makeHoleFunc = () => Number(false),
  material = {},
  show = true,
  showTooltip = false,
  transitionConfig = {},
}) {
  const [hoveredId, setHoveredId] = useState();
  const previousId = useRef();
  const MAX_DRAW_INSTANCES = 10000;

  const userDataArray = useMemo(() => data.map((d) => ({ ...d })), [data]);

  const mesh = useResource();

  // MOUSE HOVER HANDLERS ========================
  useEffect(() => {
    previousId.current = hoveredId;
    // console.log("mutating store");
    if (!mesh.current) return;
    // Uncomment if you want to enable hover callbacks
    // don't send onHover callback if element is hidden
    if (!isNaN(hoveredId)) {
      // don't show elements with a target opacity of 0
      mesh.current.geometry.attributes.tOpacity.array[hoveredId] > 0 &&
        onHover([userDataArray[hoveredId]]);
    } else {
      onHover([]);
    }
  }, [mesh, hoveredId, onHover, userDataArray]);

  const onPointerMoveCallback = useCallback(
    (e) => {
      e.stopPropagation();
      if (mesh.current.geometry.attributes.tOpacity.array[e.instanceId] > 0) {
        e.target.setPointerCapture(e.pointerId);
        setHoveredId(e.instanceId);
      }
    },
    [mesh]
  );
  const onPointerOutCallback = useCallback((e) => {
    e.stopPropagation();
    // e.target.setPointerCapture(e.pointerId);
    setHoveredId(undefined);
  }, []);
  // END MOUSE HOVER HANDLERS ========================

  // const alphaMap = useMemo(() => {
  //   let o = new Uint8Array();
  //   let tWidth = Math.ceil(Math.sqrt(data.length));
  //   let tHeight = Math.ceil(Math.sqrt(data.length));
  //   o = Uint8Array.from(
  //     new Array(tWidth * tHeight).fill(0).flatMap((_, i) => [0, 255, 0])
  //     // .flatMap((_, i) => [0, 1 - (i / (tWidth * tHeight)) * 255, 0])
  //   );
  //   return new THREE.DataTexture(
  //     o,
  //     Math.ceil(Math.sqrt(data.length)),
  //     Math.ceil(Math.sqrt(data.length)),
  //     THREE.RGBFormat
  //   );
  // }, [data]);
  const tempObject = useMemo(() => new THREE.Object3D(), []);
  const tempColor = useMemo(() => new THREE.Color(), []);
  const tempVector = useMemo(() => new THREE.Vector3(), []);

  const { showT } = useSpring({
    spring: Number(show),
    showT: show,
    config: config.gentle,
  });

  const { colorArray, opacityArray, idArray, animationDelayArray } =
    useMemoAttributeArrays({
      colorFunc,
      data,
      opacityFunc,
    });

  // a float array to be used as a boolean in the shader, indicating
  // whether we make a hold in the respective element
  const makeHoleArray = useMemo(() => {
    return Float32Array.from(data.flatMap(makeHoleFunc));
  }, [data, makeHoleFunc]);

  useTransitionAttributeArray({
    data,
    selected,
    opacityFunc,
    meshRef: mesh,
    ...transitionConfig,
  });

  const particles = useMemo(() => {
    if (!mesh.current) return;
    const particles = [];
    data.forEach((d, i) => {
      // TODO might need to push multiple targets per event
      particles.push({
        size,
        color: colorFunc(d),
        x: x(d),
        y: y(d),
        z: z(d),
        data: d,
        width: d.width,
      });
    });

    // TODO Animate instance positions in vertex shader
    particles.forEach((particle, i) => {
      let { width, x, y, z } = particle;
      tempVector.set(x, y, z);
      tempObject.position.set(x, y, z);
      // const scale = i === hoveredId ? size * 1.5 : size;
      // const scale = i === hoveredId ? 1 : 1;
      const scale = 1;

      // multiplier isn't 2, to compensate for camera distortion
      // circles should appear as circles.
      tempObject.scale.set((1.8 * width) / Math.PI, scale * size, scale * size);
      tempObject.updateMatrix();
      mesh.current.setMatrixAt(i, tempObject.matrix);
    });
    mesh.current.count = data.length;
    mesh.current.instanceMatrix.needsUpdate = true;

    return particles;
  }, [data, x, y, z, colorFunc, size, mesh, tempObject, tempVector]);

  // TODO Do these need to be updated inside useFrame?
  // TODO Only update clock inside this useFrame
  useFrame((state) => {
    if (!mesh.current) return;

    const { clock } = state;
    // alternatively use state.frame
    mesh.current.material.clock = clock.elapsedTime;

    particles.forEach((particle, i) => {
      let { color } = particle;
      if (hoveredId !== previousId.current) {
        // TODO Set color on hoveredId element
        tempColor
          .set(i === hoveredId ? 0xffffff : color)
          .toArray(colorArray, i * 3);
        mesh.current.geometry.attributes.color.needsUpdate = true;
      }
    });
  });

  return (
    <>
      {showTooltip && (
        <Tooltip data={!isNaN(hoveredId) ? userDataArray[hoveredId] : null} />
      )}
      <a.instancedMesh
        ref={mesh}
        onPointerMove={onPointerMoveCallback}
        onPointerOut={onPointerOutCallback}
        // onPointerDown={onClick}
        // onPointerMissed={onClick}
        args={[null, null, MAX_DRAW_INSTANCES]}
        userData={userDataArray}
        name={name}
        // castShadow={true}
        // receiveShadow={true}
        visible={showT}
      >
        <sphereBufferGeometry
          // ref={bufferGeometryRef}
          attach="geometry"
          args={[1, 32, 32]}
        >
          <instancedBufferAttribute
            attachObject={["attributes", "color"]}
            args={[colorArray, 3]}
          />
          <instancedBufferAttribute
            attachObject={["attributes", "instanceOpacity"]}
            args={[opacityArray, 1]}
          />
          <instancedBufferAttribute
            attachObject={["attributes", "tOpacity"]}
            args={[opacityArray, 1]}
          />
          <instancedBufferAttribute
            attachObject={["attributes", "animationDelay"]}
            args={[animationDelayArray, 1]}
          />
          <instancedBufferAttribute
            attachObject={["attributes", "instanceId"]}
            args={[idArray, 1]}
          />
          <instancedBufferAttribute
            attachObject={["attributes", "makeHole"]}
            args={[makeHoleArray, 1]}
          />
        </sphereBufferGeometry>
        {/* <a.meshStandardMaterial
          side={THREE.DoubleSide}
          transparent={true}
          blending={THREE.AdditiveBlending}
          opacity={op}
          vertexColors={THREE.VertexColors}
        /> */}
        <InstancedPhongMaterial
          attach="material"
          side={THREE.DoubleSide}
          depthTest={true}
          transparent={true}
          // alphaTest={makeHole ? 0.1 : 0}
          // alphaMap={alphaMap}
          blending={THREE.NormalBlending}
          defines={{
            USE_UV: true,
            USE_FRESNEL_SHADING: false,
            USE_MAKE_HOLE: makeHole,
          }}
          // opacity={op}
          // animationDelta={1}
          // animationStart={0}
          // clock={1}
          // roughness={1}
          // dithering={true}
          metalness={1}
          emissive={0x0}
          shininess={0}
          specular={0x0}
          // color="red"
          vertexColors={THREE.VertexColors}
          {...material}
        />
        {/* <ambientLight color={0xfff} intensity={0.3} /> */}
      </a.instancedMesh>
      {/* <primitive object={mesh.current} scale={[2.2, 2.2, 1.2]}>
        <CustomGlowMaterial vertexColors={THREE.VertexColors} />
      </primitive> */}
    </>
  );
}
